import { JSX, MouseEvent } from 'react';
import { useExpanded, useRecordContext, useResourceContext } from 'react-admin';
import { Button } from '@mui/material';

/**
 * The company row's domain count, as a button that opens the row's domains panel.
 *
 * HQ-35. react-admin's own expand toggle is `aria-hidden` with `tabIndex={-1}` (hardcoded in
 * `ExpandRowButton`), so a keyboard or screen reader user could not reach the domains at all. This
 * drives the same expansion through `useExpanded`, so the two stay in step, and points
 * `aria-controls` at the panel react-admin renders (`<id>-expand`).
 */
export const CompanyDomainsToggle: () => JSX.Element | null = (): JSX.Element | null => {
    const record: any = useRecordContext();
    const resource: string = useResourceContext() ?? 'companies';
    const [expanded, toggleExpanded] = useExpanded(resource, record?.id);

    if (!record) {
        return null;
    }

    const count: number = record.websites?.length ?? 0;
    const name: string = record.name ?? 'this company';

    return (
        <Button size="small"
                aria-expanded={ expanded }
                aria-controls={ `${ record.id }-expand` }
                aria-label={ `${ expanded ? 'Hide' : 'Show' } domains for ${ name } (${ count })` }
                onClick={ (event: MouseEvent<HTMLButtonElement>): void => {
                    // The row itself may be clickable; this button only toggles the panel.
                    event.stopPropagation();
                    toggleExpanded();
                } }>
            { count }
        </Button>
    );
};
